import { Key } from 'react';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import { ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';

export enum Ics2RowType {
    GROUP = 'GROUP',
    DECLARATION = 'DECLARATION',
}

/**
 * A single row of the ICS2 declarations listing as returned by the declarations service.
 * It is a flat summary — not a full `Declaration`.
 */
export interface Ics2DeclarationSummary {
    id: string;
    referenceId: string | null;
    mrn: string | null;
    messageType: ICS2MessageTypes | null;
    consignor: string | null;
    consignee: string | null;
    teamId: string | null;
    teamName: string | null;
    createdById: string | null;
    createdByName: string | null;
    createdAt: string | null;
    status: DeclarationStatuses | null;
    doNotLoadReceived: boolean;
    archived: boolean;
}

/**
 * A `GROUP` row summarises the declarations created together from one manifest and carries them in
 * `declarations`; a `DECLARATION` row stands on its own and has an empty `declarations` array.
 */
export interface Ics2ListRow {
    rowType: Ics2RowType;
    groupId: string | null;
    sourceManifestId: string | null;
    groupSize: number;
    expandable: boolean;
    summary: Ics2DeclarationSummary;
    declarations: Ics2DeclarationSummary[];
}

/** The listing row flattened into what the table renders — antd expands rows through `children`. */
export interface Ics2TableRow extends Ics2DeclarationSummary {
    rowType: Ics2RowType;
    groupId: string | null;
    sourceManifestId: string | null;
    groupSize: number;
    children?: Ics2TableRow[];
}

/** A single declaration as a table row, carrying the grouping its listing row was returned under. */
const toDeclarationRow = (row: Ics2ListRow, declaration: Ics2DeclarationSummary): Ics2TableRow => ({
    ...declaration,
    rowType: Ics2RowType.DECLARATION,
    groupId: row.groupId,
    sourceManifestId: row.sourceManifestId,
    groupSize: 1,
});

export const toIcs2TableRows = (list?: Ics2ListRow[]): Ics2TableRow[] =>
    (list ?? []).map((row) => {
        const children = row.declarations?.map((declaration) => toDeclarationRow(row, declaration));

        return {
            ...row.summary,
            rowType: row.rowType,
            groupId: row.groupId,
            sourceManifestId: row.sourceManifestId,
            groupSize: row.groupSize,
            ...(row.expandable && children?.length ? { children } : {}),
        };
    });

export const isIcs2GroupRow = (row?: Ics2TableRow) => row?.rowType === Ics2RowType.GROUP;

/**
 * An open group is tinted like the rows it holds, so the whole group reads as one block. Tree data
 * gives the parent row no class of its own when it opens, so the expanded keys have to say so.
 */
export const getIcs2RowClassName = (row?: Ics2TableRow, expandedRowKeys: readonly Key[] = []): string => {
    if (isIcs2GroupRow(row)) return row?.id && expandedRowKeys.includes(row.id) ? 'ics2-group-row-expanded' : '';

    return row?.rowType === Ics2RowType.DECLARATION && row.groupId ? 'ics2-group-child-row' : '';
};

export const hasIcs2Notifications = (rows: Ics2TableRow[] = []): boolean =>
    rows.some((row) => row.doNotLoadReceived || (row.children ?? []).some((child) => child.doNotLoadReceived));

const ics2MessageTypeLabels: Record<ICS2MessageTypes, string> = {
    [ICS2MessageTypes.F11]: 'F11',
    [ICS2MessageTypes.F50]: 'F50',
};

export const getIcs2MessageTypeLabel = (messageType: ICS2MessageTypes | null | undefined) =>
    messageType ? ics2MessageTypeLabels[messageType] : undefined;
